import { ref } from 'vue';
import { defineStore, storeToRefs } from 'pinia';
import type { ARTICLE_LANGUAGES } from './enums';
import type { HelpTipText, WhatsNewEntry } from './interfaces';
import { useSessionStore } from '@Stores';
import { FetchAdminWhatsNewEntriesService, FetchHelpTipTextsService, FetchWhatsNewEntriesService } from './services';

export default defineStore('whatsNew', () => {
  const { currentUserId, isSU } = storeToRefs(useSessionStore());

  let loadingKey = '';
  let queuedLanguage: ARTICLE_LANGUAGES | null = null;

  const articleEntries = ref<WhatsNewEntry[]>([]);
  const hasFailed = ref(false);
  const isLoading = ref(false);
  const isReady = ref(false);
  const loadedFor = ref('');
  const tipTexts = ref<HelpTipText[]>([]);

  // EVENTS
  // NOTE :: Pinia gives a setup store no $reset, and resetAllStores calls it on logout and on a
  // shelter switch. Without this one, both throw and the old user's entries stay in memory.
  function $reset() {
    loadingKey = '';
    queuedLanguage = null;

    articleEntries.value = [];
    hasFailed.value = false;
    isLoading.value = false;
    isReady.value = false;
    loadedFor.value = '';
    tipTexts.value = [];
  }

  function fetchEntries(lang: ARTICLE_LANGUAGES, shouldForce = false) {
    const requestKey = `${currentUserId.value}|${lang}|${isSU.value}`;
    const isAlreadyLoaded = !shouldForce && loadedFor.value === requestKey;
    if (isAlreadyLoaded) return Promise.resolve();

    // NOTE :: The app starts in the browser's language and switches to the user's own a moment
    // later, often while the first request is still out. That answer comes back in the old
    // language, so the language asked for last is loaded as soon as the first request ends.
    if (isLoading.value) {
      queuedLanguage = requestKey === loadingKey ? null : lang;

      return Promise.resolve();
    }

    isLoading.value = true;
    loadingKey = requestKey;
    hasFailed.value = false;

    // NOTE :: Super admins read the admin list. It also carries drafts, and it works for an AF
    // admin who is not logged in as a shelter.
    const request = isSU.value ? FetchAdminWhatsNewEntriesService({ lang }) : FetchWhatsNewEntriesService({ lang });

    // NOTE :: The edited titles load beside the entries. If they fail, the texts that ship in the
    // app still show, so the failure is not worth an error state.
    const tipTextsRequest = FetchHelpTipTextsService()
      .then(({ data }) => (tipTexts.value = data))
      .catch(() => (tipTexts.value = []));

    const entriesRequest = request
      .then(({ data }) => {
        articleEntries.value = data;
        loadedFor.value = requestKey;
      })
      .catch(() => (hasFailed.value = true));

    return Promise.all([entriesRequest, tipTextsRequest]).finally(onFetchEnd);
  }

  function onFetchEnd() {
    const nextLanguage = queuedLanguage;

    isLoading.value = false;
    isReady.value = true;
    queuedLanguage = null;

    if (nextLanguage) fetchEntries(nextLanguage);
  }

  function replaceEntry(updatedEntry: WhatsNewEntry) {
    const hasEntry = articleEntries.value.some(({ key }) => key === updatedEntry.key);

    articleEntries.value = hasEntry
      ? articleEntries.value.map((entry) => (entry.key === updatedEntry.key ? updatedEntry : entry))
      : [updatedEntry, ...articleEntries.value];
  }

  function replaceTipText(updatedTipText: HelpTipText) {
    tipTexts.value = [...tipTexts.value.filter(({ key }) => key !== updatedTipText.key), updatedTipText];
  }

  return {
    $reset,
    articleEntries,
    fetchEntries,
    hasFailed,
    isLoading,
    isReady,
    replaceEntry,
    replaceTipText,
    tipTexts,
  };
});
